import { Button, CountrySelect, Header, LoadingSpinner, TextInput } from '@components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { actionReport } from '@methods/actionReport'
import { axiosInstance } from '@methods/axiosConfig'

import { API_URL } from '@constants/apiUrl'
import {
  EIDV_Country,
  ElectronincIdCountriesResponse,
  mapCountriesArray,
} from '@/methods/mapCountries'
import { SelectOption } from '@components/Generic/SelectInput/SelectInput'
import { cx } from '@/utils/cx'

export const PlaceOfBirthPage = observer(() => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { currentScenarioId, context } = store.ScenarioState
  const { placeOfBirthPage: trans } = store.TranslationsState.translations

  const [placeOfBirth, setPlaceOfBirth] = useState('')
  const [country, setCountry] = useState<EIDV_Country | null>(null)
  const [apiCountries, setApiCountries] = useState<EIDV_Country[]>([])
  const [btnLoading, setBtnLoading] = useState(false)

  const getCountries = async () => {
    try {
      const res = await axiosInstance.get<ElectronincIdCountriesResponse>(
        `${API_URL}/electronic-id/countries`,
        {
          withCredentials: true,
        }
      )

      const countriesArray = mapCountriesArray(res.data)

      res.data && setApiCountries(countriesArray)
    } catch (e) {
      throw e
    }
  }

  const handleProceed = async () => {
    if (!country) return

    if (currentScenarioId !== 'localTests') {
      setBtnLoading(true)

      await axiosInstance.post(
        `${API_URL}/electronic-id/end-user-input`,
        {
          countryOfBirth: country.countryCode,
          placeOfBirth: placeOfBirth,
        },
        {
          withCredentials: true,
        }
      )
      setBtnLoading(false)
    }

    store.ScenarioState.nextPage()
  }

  const handleCountry = (country: SelectOption | null) => {
    if (country) {
      const countryApiObj =
        apiCountries.find((apiObj) => apiObj.countryCode === country.value) ?? null

      setCountry(countryApiObj)
    } else {
      setCountry(null)
    }
  }

  const handleOnBlur = () => {
    if (placeOfBirth) {
      actionReport({
        type: 'event.onboarding-web.eidv.USER_ENTERED_COUNTRY_AND_PLACE_OF_BIRTH',
        payload: {},
      })
    }
  }

  useEffect(() => {
    if (apiCountries.length === 1) {
      handleCountry(apiCountries[0])
    }
  }, [apiCountries])

  useEffect(() => {
    if (!context?.askUserForPlaceAndCountryOfBirth) {
      store.ScenarioState.skipStep()
    }

    getCountries()
  }, [])

  if (!context?.askUserForPlaceAndCountryOfBirth) {
    return (
      <div className="page-container">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container">
      <Header>{trans.header}</Header>
      <div className="generic__country-wrap">
        <span>{trans.countryOfBirth}</span>
        {isMobile ? (
          <>
            <CountrySelect
              externalList={apiCountries}
              value={country}
              onChange={handleCountry}
              autoFocus={true}
              defaultMenuIsOpen={false}
              placeholder={trans.inputPlaceholder}
              showCheckmark
            />
          </>
        ) : (
          <>
            <CountrySelect
              externalList={apiCountries}
              value={country}
              onChange={handleCountry}
              autoFocus={false}
              defaultMenuIsOpen={false}
              placeholder={trans.inputPlaceholder}
              showCheckmark
            />
          </>
        )}
      </div>

      <div className="generic__text-input-wrap">
        <span>{trans.placeOfBirth}</span>
        <TextInput
          type="text"
          value={placeOfBirth}
          onChange={(value: string) => {
            setPlaceOfBirth(value)
          }}
          onBlur={handleOnBlur}
          disabled={!country}
          id="place-of-birth-input"
        />
      </div>

      <div className="layout__content-container-bottom-fixed">
        {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
        <Button
          onClick={() => handleProceed()}
          disabled={!(placeOfBirth && placeOfBirth.length)}
          id="page-submit"
        >
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          >
            {trans.proceed}
          </span>
        </Button>
      </div>
    </div>
  )
})
